import config from '../config/api';

class AIRecommendationService {
  static pick(obj, keys) {
    if (!obj || typeof obj !== 'object') return {};
    return keys.reduce((acc, key) => {
      if (obj[key] !== undefined) acc[key] = obj[key];
      return acc;
    }, {});
  }

  static truncateText(value, maxLen = 500) {
    if (typeof value !== 'string') return value;
    if (value.length <= maxLen) return value;
    return `${value.slice(0, maxLen)}...`;
  }

  static slimTransactions(transactions) {
    if (!Array.isArray(transactions)) return [];
    return transactions.slice(0, 20).map((tx) => ({
      address: tx?.address,
      distance: tx?.distance,
      date: tx?.date,
      amount: tx?.amount,
      size: tx?.size,
      beds: tx?.beds ?? tx?.rooms,
      rooms: tx?.rooms,
      pricePerMeter: tx?.pricePerMeter,
    }));
  }

  static slimArchitecturalPlans(plans) {
    if (!Array.isArray(plans)) return [];
    return plans.slice(0, 150).map((plan) => ({
      title: plan?.title,
      planNumber: plan?.planNumber,
      status: plan?.status,
      planType: plan?.planType,
      planSubtype: plan?.planSubtype,
      permitType: plan?.permitType,
      requestType: plan?.requestType,
      submissionDate: plan?.submissionDate,
      approvalDate: plan?.approvalDate,
      source: plan?.source,
      type: plan?.type,
      isPriority: plan?.isPriority,
      priorityType: plan?.priorityType,
      isTelAvivMunicipal: plan?.isTelAvivMunicipal,
      isTelAvivPermit: plan?.isTelAvivPermit,
      PERMIT_NUMBER: plan?.PERMIT_NUMBER,
      PERMIT_NAME: plan?.PERMIT_NAME,
      PERMIT_STATUS: plan?.PERMIT_STATUS,
      PLAN_NAME: plan?.PLAN_NAME,
      PLAN_NUMBER: plan?.PLAN_NUMBER,
      TOKHNIT_SHEM: plan?.TOKHNIT_SHEM,
      TOKHNIT_MISPAR: plan?.TOKHNIT_MISPAR,
      PLAN_STATUS: plan?.PLAN_STATUS,
      STATUS: plan?.STATUS,
      CATEGORY_NAME: plan?.CATEGORY_NAME,
      address: plan?.address,
      requestNumber: plan?.requestNumber,
      objectives: AIRecommendationService.truncateText(plan?.objectives, 180),
      description: AIRecommendationService.truncateText(plan?.description, 140),
      landUse: plan?.landUse,
    }));
  }

  static slimBuildingInfo(buildingInfo) {
    return AIRecommendationService.pick(buildingInfo, [
      'constructionYear',
      'confidence',
      'floors',
      'floorsOverrideReason',
      'height',
      'area',
      'buildingType',
      'landUse',
      'tama38Zone',
      'buildingConservationStatus',
      'section7778Plans',
      'section23Plans',
    ]);
  }

  static slimKpis(kpis) {
    return AIRecommendationService.pick(kpis, [
      'totalExistingAreaSqm',
      'estimatedNewUnits',
      'totalNewGfaSqm',
      'pricePerSqmILS',
      'totalProjectCostILS',
      'totalRevenueILS',
      'netProfitILS',
      'roiPercent',
    ]);
  }

  static slimBuildingData(buildingData) {
    return AIRecommendationService.pick(buildingData || {}, [
      'floors',
      'floorsOverrideReason',
      'height',
      'area',
      'hasColumns',
      'buildingType',
      'year',
      'yearEstimated',
      'yearNote',
      'conservationSite',
      'conservationArea',
      'buildingFileNumber',
    ]);
  }

  static buildSlimPayload(feasibilityData, searchResults, address) {
    const feasibilityInput = feasibilityData?.feasibilityInput || {};
    const feasibilityResult = feasibilityData?.feasibilityResult || {};
    const buildingInfo = AIRecommendationService.slimBuildingInfo(feasibilityResult?.buildingInfo || {});
    const kpis = AIRecommendationService.slimKpis(feasibilityResult?.kpis || {});

    const marketData = feasibilityInput?.marketData || {};
    const slimMarketData = {
      ...AIRecommendationService.pick(marketData, ['pricePerMeter', 'averagePrice', 'priceRange', 'lastUpdated']),
      transactions: AIRecommendationService.slimTransactions(marketData.transactions),
    };

    const slimFeasibilityInput = {
      ...AIRecommendationService.pick(feasibilityInput, [
        'constructionYear',
        'constructionYearConfidence',
        'pre1980Eligibility',
        'declaredPinuyBinuyArea',
        'section7778Plans',
        'section23Plans',
        'buildingConservationStatus',
        'conservationListed',
        'landUse',
        'municipalityName',
        'gush',
        'helka',
        'lotAreaSqm',
        'existingUnits',
        'avgExistingUnitAreaSqm',
      ]),
      section7778Plans: Array.isArray(feasibilityInput.section7778Plans)
        ? feasibilityInput.section7778Plans.slice(0, 20)
        : [],
      section23Plans: Array.isArray(feasibilityInput.section23Plans)
        ? feasibilityInput.section23Plans.slice(0, 20)
        : [],
      marketData: slimMarketData,
    };

    const slimFeasibilityResult = {
      ...AIRecommendationService.pick(feasibilityResult, [
        'tama38Eligible',
        'tama38ValidInMunicipality',
        'declaredPinuyBinuyArea',
        'conservationListed',
      ]),
      buildingInfo,
      kpis,
    };

    const propertyInfo = searchResults?.propertyInfo || {};
    const advancedLayersTop = searchResults?.advancedLayers || {};
    const advancedLayers = propertyInfo?.advancedLayers || advancedLayersTop || {};

    const slimAdvancedLayers = {
      buildingStats: advancedLayers?.buildingStats || null,
      socioEconomic: advancedLayers?.socioEconomic || null,
      landUse: advancedLayers?.landUse || null,
      municipality: advancedLayers?.municipality || null,
      buildingAreaOnParcel: advancedLayers?.buildingAreaOnParcel || null,
      ownership: Array.isArray(advancedLayers?.ownership)
        ? advancedLayers.ownership.slice(0, 3)
        : [],
    };

    const slimSearchResults = {
      coordinates: searchResults?.coordinates || null,
      architecturalPlans: AIRecommendationService.slimArchitecturalPlans(searchResults?.architecturalPlans),
      buildingData: AIRecommendationService.slimBuildingData(searchResults?.buildingData),
      propertyInfo: {
        ...AIRecommendationService.pick(propertyInfo, ['municipality', 'neighborhood', 'parcel', 'block', 'gush', 'helka']),
        buildingData: AIRecommendationService.slimBuildingData(propertyInfo?.buildingData),
        advancedLayers: slimAdvancedLayers,
      },
      advancedLayers: slimAdvancedLayers,
      sourceHealth: searchResults?.sourceHealth || null,
    };

    return {
      feasibilityData: {
        feasibilityInput: slimFeasibilityInput,
        feasibilityResult: slimFeasibilityResult,
      },
      searchResults: slimSearchResults,
      address,
    };
  }

  static async getRecommendation(feasibilityData, searchResults, address, options = {}) {
    try {
      console.log('🤖 Requesting AI recommendation...');
      const { aiAccessToken, idempotencyKey } = options;

      if (!aiAccessToken) {
        return {
          success: false,
          error: 'Missing AI access token'
        };
      }
      
      const payload = AIRecommendationService.buildSlimPayload(feasibilityData, searchResults, address);
      const payloadSize = new Blob([JSON.stringify(payload)]).size;
      console.log(`📦 AI request payload size: ${(payloadSize / 1024).toFixed(1)} KB`);

      const response = await fetch(`${config.apiBaseUrl}/api/ai-recommendation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${aiAccessToken}`,
          'Idempotency-Key': idempotencyKey || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`),
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(async () => ({
          error: await response.text(),
        }));
        throw new Error(errorData.error || 'Failed to get AI recommendation');
      }

      const data = await response.json();
      console.log('✅ AI recommendation received');
      
      return {
        success: true,
        recommendation: data.recommendation
      };
    } catch (error) {
      console.error('❌ Error getting AI recommendation:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default AIRecommendationService;
